import React, { useEffect, useState } from "react";
import { FiCopy, FiRefreshCw, FiTrash2, FiCheck, FiLink } from "react-icons/fi";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import { useTheme } from "../context/ThemeContext";

const ERPIntegration = () => {
    const { mode } = useTheme();
    const isDark = mode === "dark";

    const [connection, setConnection] = useState(null);
    const [generatedToken, setGeneratedToken] = useState("");
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [copied, setCopied] = useState(false);

    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    const loadStatus = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}erp/connection/status/`, { headers });
            const data = await res.json();
            if (data.status === "success") {
                setConnection(data.data);
            }
        } catch (err) {
            console.error("Failed to load ERP status:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}erp/connection/generate/`, {
                method: "POST",
                headers,
            });
            const data = await res.json();
            if (data.status === "success") {
                setGeneratedToken(data.data.token);
                showSuccessToast("Token generated. Copy and paste it in the ERP.");
                loadStatus();
            } else {
                showErrorToast(data.message || "Failed to generate token");
            }
        } catch (err) {
            showErrorToast("Failed to generate token");
        } finally {
            setGenerating(false);
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm("Revoke the ERP connection? The ERP will lose access to FixDesk data.")) return;
        setRevoking(true);
        try {
            const res = await fetch(`${API_BASE_URL}erp/connection/revoke/`, {
                method: "POST",
                headers,
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast("ERP connection revoked");
                setConnection(null);
                setGeneratedToken("");
                loadStatus();
            } else {
                showErrorToast(data.message || "Failed to revoke");
            }
        } catch (err) {
            showErrorToast("Failed to revoke connection");
        } finally {
            setRevoking(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedToken);
        setCopied(true);
        showSuccessToast("Token copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
    };

    const s = getStyles(isDark);

    if (loading) {
        return (
            <div style={s.container}>
                <h1 style={s.title}>ERP Integration</h1>
                <p style={s.loading}>Loading...</p>
            </div>
        );
    }

    return (
        <div style={s.container}>
            <h1 style={s.title}>ERP Integration</h1>
            <p style={s.subtitle}>
                Connect FixDesk to your ERP platform to share ticket metrics, sprint progress, and team workload.
            </p>

            {/* Connection Status */}
            <div style={s.card}>
                <div style={s.cardHeader}>
                    <FiLink size={18} style={{ color: connection?.is_active ? "#22c55e" : "#888" }} />
                    <h3 style={s.cardTitle}>Connection Status</h3>
                </div>

                {connection && connection.is_active ? (
                    <div>
                        <div style={s.statusRow}>
                            <span style={s.statusBadgeActive}>Connected</span>
                        </div>
                        <div style={s.detailGrid}>
                            <div style={s.detailItem}>
                                <span style={s.detailLabel}>Organization</span>
                                <span style={s.detailValue}>{connection.org_name}</span>
                            </div>
                            <div style={s.detailItem}>
                                <span style={s.detailLabel}>API Key</span>
                                <span style={{ ...s.detailValue, fontFamily: "monospace" }}>{connection.api_key}</span>
                            </div>
                            <div style={s.detailItem}>
                                <span style={s.detailLabel}>Last Connected</span>
                                <span style={s.detailValue}>
                                    {connection.last_connected_at
                                        ? new Date(connection.last_connected_at).toLocaleString()
                                        : "Never"}
                                </span>
                            </div>
                        </div>
                        <button
                            style={s.revokeBtn}
                            onClick={handleRevoke}
                            disabled={revoking}
                        >
                            <FiTrash2 size={14} />
                            {revoking ? "Revoking..." : "Revoke Connection"}
                        </button>
                    </div>
                ) : (
                    <p style={s.noConnection}>No active connection. Generate a token below.</p>
                )}
            </div>

            {/* Generate Token */}
            <div style={s.card}>
                <div style={s.cardHeader}>
                    <FiRefreshCw size={18} style={{ color: "#7c5cfc" }} />
                    <h3 style={s.cardTitle}>Generate Connection Token</h3>
                </div>

                <div style={s.steps}>
                    <p style={s.stepsTitle}>How it works:</p>
                    <ol style={s.stepsList}>
                        <li>Click <strong>Generate Token</strong> below</li>
                        <li>Copy the generated token</li>
                        <li>Go to your ERP &rarr; Settings &rarr; Connect FixDesk</li>
                        <li>Paste the token and connect</li>
                    </ol>
                </div>

                <button
                    style={s.generateBtn}
                    onClick={handleGenerate}
                    disabled={generating}
                >
                    <FiRefreshCw size={14} style={generating ? { animation: "spin 1s linear infinite" } : {}} />
                    {generating ? "Generating..." : "Generate Token"}
                </button>

                {generatedToken && (
                    <div style={s.tokenBox}>
                        <div style={s.tokenHeader}>
                            <span style={s.tokenLabel}>Connection Token</span>
                            <button style={s.copyBtn} onClick={handleCopy}>
                                {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <textarea
                            style={s.tokenTextarea}
                            value={generatedToken}
                            readOnly
                            rows={4}
                            onClick={(e) => e.target.select()}
                        />
                        <p style={s.tokenWarning}>
                            This token contains your API key. Keep it secure and do not share publicly.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const getStyles = (isDark) => ({
    container: {
        padding: "24px 32px",
        minHeight: "100vh",
        backgroundColor: isDark ? "#000" : "#f5f5f9",
        color: isDark ? "#e0e0e0" : "#1a1a2e",
        maxWidth: 720,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        marginBottom: 4,
        color: isDark ? "#fff" : "#1a1a2e",
    },
    subtitle: {
        fontSize: 14,
        color: isDark ? "#999" : "#666",
        marginBottom: 24,
    },
    loading: { fontSize: 16, color: "#888" },
    card: {
        backgroundColor: isDark ? "#1e1e1e" : "#fff",
        borderRadius: 10,
        padding: "20px 24px",
        marginBottom: 20,
        border: `1px solid ${isDark ? "#333" : "#e0e0e8"}`,
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
    },
    cardTitle: {
        fontSize: 16,
        fontWeight: 600,
        margin: 0,
        color: isDark ? "#fff" : "#1a1a2e",
    },
    statusRow: { marginBottom: 16 },
    statusBadgeActive: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)",
        color: "#22c55e",
        border: "1px solid rgba(34,197,94,0.3)",
    },
    detailGrid: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 },
    detailItem: { display: "flex", flexDirection: "column", gap: 2 },
    detailLabel: { fontSize: 12, fontWeight: 500, color: isDark ? "#888" : "#999" },
    detailValue: { fontSize: 14, color: isDark ? "#ddd" : "#333" },
    noConnection: { fontSize: 14, color: isDark ? "#888" : "#999" },
    revokeBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 16px",
        borderRadius: 6,
        border: "1px solid #ef4444",
        backgroundColor: "transparent",
        color: "#ef4444",
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
    },
    steps: {
        backgroundColor: isDark ? "rgba(124,92,252,0.08)" : "rgba(124,92,252,0.05)",
        borderRadius: 8,
        padding: "12px 16px",
        marginBottom: 16,
        border: `1px solid ${isDark ? "rgba(124,92,252,0.2)" : "rgba(124,92,252,0.15)"}`,
    },
    stepsTitle: { fontSize: 13, fontWeight: 600, marginBottom: 8, color: isDark ? "#c4b5fd" : "#6d28d9" },
    stepsList: {
        margin: 0,
        paddingLeft: 20,
        fontSize: 13,
        lineHeight: 1.8,
        color: isDark ? "#bbb" : "#555",
    },
    generateBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "10px 20px",
        borderRadius: 6,
        border: "none",
        backgroundColor: "#7c5cfc",
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
    },
    tokenBox: {
        marginTop: 16,
        backgroundColor: isDark ? "#111" : "#f9f9fb",
        borderRadius: 8,
        padding: 16,
        border: `1px solid ${isDark ? "#333" : "#e0e0e8"}`,
    },
    tokenHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8,
    },
    tokenLabel: { fontSize: 13, fontWeight: 600, color: isDark ? "#ccc" : "#333" },
    copyBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 10px",
        borderRadius: 4,
        border: `1px solid ${isDark ? "#555" : "#ccc"}`,
        backgroundColor: "transparent",
        color: isDark ? "#ddd" : "#333",
        fontSize: 12,
        cursor: "pointer",
    },
    tokenTextarea: {
        width: "100%",
        padding: 10,
        borderRadius: 6,
        border: `1px solid ${isDark ? "#444" : "#ddd"}`,
        backgroundColor: isDark ? "#1e1e1e" : "#fff",
        color: isDark ? "#ddd" : "#333",
        fontFamily: "monospace",
        fontSize: 12,
        resize: "none",
        outline: "none",
        boxSizing: "border-box",
    },
    tokenWarning: {
        fontSize: 11,
        color: "#f59e0b",
        marginTop: 8,
        marginBottom: 0,
    },
});

export default ERPIntegration;
